import { useCallback, useMemo } from 'react';

import { MissingReplacementsError, replace } from '@change-corgi/core/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';

import type { TranslationName } from './useCopyI18nKeys';
import { useReplacements } from './useReplacements';

type Result = ModelHookResult<
	{ copy: Partial<Record<TranslationName, string>> },
	{ processLabel: (label: string) => string }
>;

export function useBanditCopy(): Result {
	const {
		data: { bandits },
	} = usePsfSharePageData();
	const {
		data: { replacements },
	} = useReplacements();
	const {
		errorReporter: { report },
	} = useUtilityContext();

	return {
		data: {
			copy: useMemo(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				() => (bandits.psfShareCopy.variant?.data || {}) as Partial<Record<TranslationName, string>>,
				[bandits],
			),
		},
		actions: {
			processLabel: useCallback(
				(label: string): string => {
					try {
						return replace(label, replacements);
					} catch (e) {
						if (e instanceof MissingReplacementsError) {
							void report({
								error: e,
								params: { page: 'psf/share', variant: bandits.psfShareCopy.variant?.variantName },
							});
							return e.finalTranslation;
						}
						throw e;
					}
				},
				[replacements, report, bandits],
			),
		},
	};
}
